import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '@/components/Header';
import {
  Gear,
  Envelope,
  Clock,
  CalendarBlank,
  GlobeHemisphereWest,
  CheckCircle,
  PaperPlaneRight,
  SpinnerGap
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const DAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const TIMES = [
  '06:00', '06:30', '07:00', '07:30', '08:00', '08:30',
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00',
];

const TIMEZONES = [
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Mumbai (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'UTC', label: 'UTC' },
];

function formatTime12h(time24) {
  const [h, m] = time24.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

function Settings({ user }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchPrefs();
  }, []);

  const fetchPrefs = async () => {
    try {
      const res = await axios.get(`${API}/user/preferences`, { withCredentials: true });
      setPrefs(res.data);
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const updatePref = async (field, value) => {
    const updated = { ...prefs, [field]: value };
    setPrefs(updated);
    setSaving(true);
    setSaved(false);
    try {
      const res = await axios.put(`${API}/user/preferences`, { [field]: value }, { withCredentials: true });
      setPrefs(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save preference:', err);
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/send-weekly-summary`, {}, { withCredentials: true });
      setTestResult({ status: 'success', message: `Email sent to ${res.data.to}` });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send';
      setTestResult({ status: 'error', message: detail });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header user={user} />
        <div className="flex justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-2xl mx-auto p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-1 flex items-center gap-3">
            <Gear size={32} weight="duotone" className="text-navy" />
            Settings
          </h1>
          <p className="text-sm md:text-base text-slate-500">Manage your notification preferences</p>
        </div>

        {/* Weekly Summary Section */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-lg overflow-hidden"
          data-testid="weekly-summary-settings">

          {/* Section header */}
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Envelope size={20} weight="duotone" className="text-navy" />
              </div>
              <div>
                <h2 className="text-base font-heading font-medium text-slate-900">Weekly Summary Email</h2>
                <p className="text-xs text-slate-400">Get a digest of your completed, upcoming, and overdue tasks</p>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-6">
            {/* Toggle */}
            <div className="flex items-center justify-between" data-testid="weekly-toggle-row">
              <div>
                <p className="text-sm font-medium text-slate-900">Weekly Summary</p>
                <p className="text-xs text-slate-400">Receive a weekly email with your task activity</p>
              </div>
              <button
                onClick={() => updatePref('weekly_email_enabled', !prefs?.weekly_email_enabled)}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                  prefs?.weekly_email_enabled ? 'bg-navy' : 'bg-slate-200'
                }`}
                data-testid="weekly-toggle"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                  prefs?.weekly_email_enabled ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>

            {/* Preferences (only show when enabled) */}
            {prefs?.weekly_email_enabled && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4 pt-2 border-t border-slate-100">

                {/* Preferred day */}
                <div data-testid="preferred-day-row">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                    <CalendarBlank size={16} className="text-slate-400" />
                    Preferred day
                  </label>
                  <select
                    value={prefs?.preferred_day || 'monday'}
                    onChange={(e) => updatePref('preferred_day', e.target.value)}
                    className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
                    data-testid="preferred-day-select"
                  >
                    {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>

                {/* Preferred time */}
                <div data-testid="preferred-time-row">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                    <Clock size={16} className="text-slate-400" />
                    Preferred time
                  </label>
                  <select
                    value={prefs?.preferred_time || '09:00'}
                    onChange={(e) => updatePref('preferred_time', e.target.value)}
                    className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
                    data-testid="preferred-time-select"
                  >
                    {TIMES.map(t => <option key={t} value={t}>{formatTime12h(t)}</option>)}
                  </select>
                </div>

                {/* Timezone */}
                <div data-testid="timezone-row">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                    <GlobeHemisphereWest size={16} className="text-slate-400" />
                    Timezone
                  </label>
                  <select
                    value={prefs?.timezone || 'Europe/London'}
                    onChange={(e) => updatePref('timezone', e.target.value)}
                    className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
                    data-testid="timezone-select"
                  >
                    {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  </select>
                </div>
              </motion.div>
            )}

            {/* Save confirmation */}
            {saved && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-sm text-emerald-600" data-testid="save-confirmation">
                <CheckCircle size={16} weight="fill" />
                <span>Preferences saved</span>
              </motion.div>
            )}
          </div>

          {/* Test send section */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500">Test your weekly summary</p>
                <p className="text-[11px] text-slate-400">Send a preview to {user?.email}</p>
              </div>
              <button
                onClick={sendTestEmail}
                disabled={sendingTest}
                className="bg-white text-slate-600 border border-slate-200 hover:bg-slate-100 transition-all duration-200 font-medium px-3.5 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                data-testid="send-test-email-btn"
              >
                {sendingTest ? (
                  <><SpinnerGap size={14} className="animate-spin" /> Sending...</>
                ) : (
                  <><PaperPlaneRight size={14} weight="fill" /> Send Test</>
                )}
              </button>
            </div>
            {testResult && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className={`mt-2 text-xs ${testResult.status === 'success' ? 'text-emerald-600' : 'text-red-600'}`}
                data-testid="test-result">
                {testResult.message}
              </motion.p>
            )}
          </div>
        </motion.div>

        {/* Account info */}
        <div className="mt-6 bg-white border border-slate-200 rounded-lg px-6 py-5" data-testid="account-info">
          <h2 className="text-base font-heading font-medium text-slate-900 mb-3">Account</h2>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Name</span>
              <span className="text-slate-900 font-medium">{user?.name}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Email</span>
              <span className="text-slate-900 font-medium">{user?.email}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Settings;
